import { useAppTranslation } from '../i18n/useAppTranslation';
import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Send } from "@mui/icons-material";

export default function MessageComposer({
  onSend,
  placeholder = "Écrivez votre message…",
}: {
  onSend: (content: string) => Promise<void>;
  placeholder?: string;
}) {
  const { tr } = useAppTranslation();
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const locked = useRef(false);
  const send = async () => {
    const content = draft.trim();
    if (!content || locked.current) return;
    locked.current = true;
    setSending(true);
    setError(false);
    try {
      await onSend(content);
      setDraft("");
    } catch {
      setError(true);
    } finally {
      locked.current = false;
      setSending(false);
    }
  };
  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
           {tr("Message non envoyé. Votre texte est conservé, vous pouvez réessayer.")} </Alert>
      )}
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
        <TextField
          fullWidth
          multiline
          maxRows={6}
          value={draft}
          placeholder={placeholder}
          inputProps={{ "aria-label": tr("Votre message"), readOnly: sending }}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing
            ) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <IconButton
          type="submit"
          aria-label={sending ? "Envoi en cours" : tr("Envoyer le message")}
          disabled={sending || !draft.trim()}
          color="primary"
          sx={{ mb: 0.5 }}
        >
          {sending ? <CircularProgress size={22} color="inherit" /> : <Send />}
        </IconButton>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: { xs: "none", sm: "block" }, mt: 0.75 }}
      >
         {tr("Entrée pour envoyer · Maj + Entrée pour une nouvelle ligne")} </Typography>
    </Box>
  );
}
